import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Headphones, Mic, MicOff, PhoneOff, UserRound, Wrench } from "lucide-react";
import { useParkingCall } from "@/hooks/useParkingCall";

/**
 * Kiosk landing page.
 *
 * URL params (typically injected by the QR code stuck on the pillar):
 *   ?site=NL-AMS-01      site identifier (defaults to "demo")
 *   ?loc=Damrak%20P1     human-readable location shown on screen + told to Fenna
 *   ?mach=K3             machine / pillar id
 *   ?debug=1             show transcript overlay
 *   ?kiosk=1             hide any developer chrome (currently a no-op)
 */
export default function KioskPage() {
    const params = useMemo(() => new URLSearchParams(window.location.search), []);
    const siteId = params.get("site") || "demo";
    const location = params.get("loc") || "";
    const machine = params.get("mach") || "";
    const debug = params.get("debug") === "1";

    const kioskCtx = useMemo(
        () => ({ siteId, location: location || undefined, machine: machine || undefined }),
        [siteId, location, machine],
    );
    const { state, error, lastToolCall, transcript, operatorName, start, stop } = useParkingCall(kioskCtx);
    const [pressedAt, setPressedAt] = useState<number | null>(null);

    // Auto-clear elapsed timer once we're back to idle after a call.
    useEffect(() => {
        if (state === "idle" || state === "ended") setPressedAt(null);
    }, [state]);

    const active = state !== "idle" && state !== "ended" && state !== "error";
    const busyLabel: Record<typeof state, string> = {
        idle: "",
        connecting: "Verbinden…",
        listening: "Ik luister…",
        thinking: `Even kijken${lastToolCall ? ` (${lastToolCall})` : ""}…`,
        speaking: "Fenna spreekt",
        escalating: "Ik verbind je door met een medewerker…",
        bridged: `${operatorName ?? "Medewerker"} is aan de lijn`,
        ended: "Gesprek beëindigd",
        error: "Er ging iets mis",
    } as const;

    const handlePress = async () => {
        setPressedAt(Date.now());
        await start();
    };

    return (
        <div className="relative flex h-full w-full flex-col items-center justify-between bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 p-6 sm:p-10">
            {/* Top strip: site + status */}
            <header className="flex w-full items-center justify-between text-sm text-gray-400">
                <div>
                    <span className="font-semibold text-parking-300">parking.audiona.ai</span>
                    <span className="ml-2 text-gray-500">·</span>
                    <span className="ml-2">{location || `Site ${siteId}`}</span>
                    {machine ? <span className="ml-2 text-gray-500">· paal {machine}</span> : null}
                </div>
                <div className="flex items-center gap-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${state === "error" ? "bg-red-500" : active ? "bg-parking-400" : "bg-gray-600"}`} />
                    <span>{state === "idle" ? "Stand-by" : busyLabel[state]}</span>
                </div>
            </header>

            {/* Middle: the big button */}
            <main className="flex flex-1 flex-col items-center justify-center">
                {!active ? (
                    <button
                        onClick={handlePress}
                        className="group relative flex h-72 w-72 items-center justify-center rounded-full bg-parking-600 shadow-2xl transition hover:bg-parking-500 focus:outline-none focus:ring-8 focus:ring-parking-500/40 active:scale-95 sm:h-96 sm:w-96"
                        aria-label="Druk voor hulp"
                    >
                        <span className="pointer-events-none absolute -inset-6 rounded-full bg-parking-500/25 animate-halo" />
                        <div className="relative flex flex-col items-center gap-3 text-white">
                            <Mic className="h-16 w-16 sm:h-20 sm:w-20" strokeWidth={1.4} />
                            <span className="text-3xl font-bold sm:text-4xl">Druk voor hulp</span>
                            <span className="text-base text-parking-100/90 sm:text-lg">Press for help</span>
                        </div>
                    </button>
                ) : (
                    <div className="flex flex-col items-center gap-8">
                        <div className={`relative flex h-56 w-56 items-center justify-center rounded-full bg-parking-500/20 ring-4 ring-parking-400/60 sm:h-72 sm:w-72 ${state === "listening" || state === "bridged" ? "animate-mic-pulse" : ""}`}>
                            {state === "thinking" ? (
                                <div className="flex flex-col items-center gap-3 text-parking-100">
                                    <Wrench className="h-14 w-14" strokeWidth={1.4} />
                                    <div className="flex gap-1.5 text-2xl">
                                        <span className="dot-thinking">•</span>
                                        <span className="dot-thinking">•</span>
                                        <span className="dot-thinking">•</span>
                                    </div>
                                </div>
                            ) : state === "escalating" ? (
                                <div className="flex flex-col items-center gap-3 text-parking-100">
                                    <Headphones className="h-14 w-14 animate-pulse" strokeWidth={1.4} />
                                </div>
                            ) : state === "bridged" ? (
                                <UserRound className="h-20 w-20 text-parking-100 sm:h-24 sm:w-24" strokeWidth={1.4} />
                            ) : (
                                <Mic className="h-20 w-20 text-parking-100 sm:h-24 sm:w-24" strokeWidth={1.4} />
                            )}
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-semibold text-white sm:text-3xl">{busyLabel[state]}</div>
                            {pressedAt && (
                                <div className="mt-1 text-sm text-gray-400">Gesprek loopt · <ElapsedTimer since={pressedAt} /></div>
                            )}
                        </div>
                        <button
                            onClick={stop}
                            className="flex items-center gap-3 rounded-full bg-red-600 px-8 py-4 text-lg font-semibold text-white shadow-lg transition hover:bg-red-500 focus:outline-none focus:ring-4 focus:ring-red-500/40 active:scale-95"
                        >
                            <PhoneOff className="h-6 w-6" /> Gesprek beëindigen
                        </button>
                    </div>
                )}
            </main>

            {/* Bottom: error or footer */}
            <footer className="w-full text-center text-xs text-gray-500">
                {error ? (
                    <div className="mx-auto flex max-w-md items-center justify-center gap-2 rounded-lg bg-red-900/40 px-4 py-2 text-red-300">
                        <AlertCircle className="h-4 w-4" />
                        <span>{error}</span>
                    </div>
                ) : (
                    <span>
                        <MicOff className="mr-1 inline h-3 w-3" />
                        Geen microfoontoegang zonder druk op de knop · your audio is not recorded
                    </span>
                )}
            </footer>

            {/* Debug: live transcript overlay */}
            {debug && transcript.length > 0 && (
                <div className="pointer-events-none absolute inset-x-4 bottom-16 mx-auto max-h-64 max-w-2xl overflow-y-auto rounded-xl border border-gray-800 bg-gray-900/85 p-4 text-left text-sm shadow-xl backdrop-blur">
                    {transcript.slice(-6).map((turn, i) => (
                        <div key={i} className={turn.who === "fenna" ? "text-parking-200" : "text-gray-100"}>
                            <span className="mr-2 text-xs font-semibold uppercase text-gray-500">
                                {turn.who === "fenna" ? "Fenna" : "Caller"}
                            </span>
                            {turn.text}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ElapsedTimer({ since }: { since: number }) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const h = window.setInterval(() => setNow(Date.now()), 500);
        return () => window.clearInterval(h);
    }, []);
    const s = Math.floor((now - since) / 1000);
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return <span>{mm}:{ss}</span>;
}
